import { User, getAuth } from "firebase/auth";
import { getUserData } from "./api";

//===========================================================================
// User
//===========================================================================

export type UserData = {
  downloadable?: boolean
  credit?: number;
};

//===========================================================================
// Face
//===========================================================================

export type DetectedFace = {
  userId: undefined;
  faceId: undefined;
  croppedFace: string;
  meshedFace: string;
} | {
  userId: string;
  faceId: string;
  croppedFace: undefined;
  meshedFace: undefined;
} | {
  userId: string;
  faceId: string;
  croppedFace: string;
  meshedFace: string;
};

export type Face = {
  createdAt: number;
  userId: string;
  faceId: string;
  hash?: string;
} & DetectedFace;

//===========================================================================
// Original
//===========================================================================

export interface DetectedOriginal {
  url: string;
  detected: {
    imageUrl: string;
    title: string;
  }
}

export interface Original extends DetectedOriginal {
  userId: string;
  originalId: string;
  createdAt: number;
}

// 素材動画の並び順。`/detect_video` のたびに `Original` が作られるため、
// 同じ URL のドキュメントが複数ある。「最後に使った日」は最新の作成日、
// 「追加した日」は最古の作成日にあたる。
export type OriginalSort = "lastUsed" | "newest" | "oldest";

//===========================================================================
// Swap
//===========================================================================

export enum SwapStatus {
  PENDING = "pending",
  STARTED_GENERATING = "started_generating",
  GENERATED = "generated"
}

export enum SwapSource {
  MANUAL = "manual",
  CATALOG = "catalog"
}

// カタログをどの面で見せたか。面ごとの転換率を比べるためにサーバーへ送る。
export enum CatalogPlacement {
  WATCH = "watch",
  HOME = "home"
}

interface SwapDocument {
  userId: string;
  swapId: string;
  faceId: string;
  streamingId: string;
  originalUrl: string;
  createdAt: number;
  status: SwapStatus;
  // カタログ導線から作られた場合のみ入る。既存の Swap には無い。
  source?: SwapSource;
  sceneId?: string;
  placement?: CatalogPlacement;
}

export type Swap = SwapDocument & {
  original: Original;
  face: Face;
};

//===========================================================================
// Scene catalog
//===========================================================================

// カタログのタイル 1 枚。thumbnailUrl は選択中の顔に差し替えたサムネイル。
export interface CatalogScene {
  sceneId: string;
  title: string;
  tags: string[];
  thumbnailUrl: string;
  // 顔の差し替えが終わっているか。false の間はプレースホルダを出す。
  ready: boolean;
}

// 棚はユーザー自身の履歴から作るので、並び順も本人の使用状況で決まる。
export type CatalogSort = "used" | "recent";

export interface CreditPack {
  packId: string;
  credits: number;
  amountUsd: number;
  unitAmountUsd: number;
  // 動画 1 本の通常価格と、そこからの割引率。どちらもサーバーが Stripe の
  // Price から算出する（フロントに価格を焼き込まないため）。
  unitPriceUsd: number | null;
  discountPercent: number | null;
}

// パック決済の確認結果。`already_granted` は再読み込み等で 2 回目以降に呼んだ場合で、
// 付与自体は成功しているので UI では成立として扱う。
export type CreditPackStatus = "granted" | "already_granted" | "pending";

//===========================================================================
// Temporary Swap
//===========================================================================

export interface TemporarySwapRequest {
  originalUrl: string;
  originalThumbnailUrl: string;
  originalTitle: string;
  croppedFace: string;
  meshedFace: string;
  baseUrl: string;
}

export interface TemporarySwap {
  swapRequest: TemporarySwapRequest;
}

//===========================================================================
// Streaming
//===========================================================================

export interface Streaming {
  userId: string;
  streamingId: string;
  status: "generated"|"generating";
  license: string;
  // サーバーは base64 で返し、api.ts で復号する。
  manifest: { [key: string]: Uint8Array }
};

//===========================================================================
// User
//===========================================================================

export class UserState {
  _user: User | null | undefined;
  _userData: UserData | null;
  _onUpdate?: () => void;

  constructor(user: User|null|undefined, onUpdate?: () => void) {
    this._user = user;
    this._onUpdate = onUpdate;
    this._userData = null;
  }

  update() {
    this._user = getAuth().currentUser;
    if (this._onUpdate) {
      this._onUpdate();
    }
  }

  async getUserData() {
    if (!this.user) {
      return null;
    }
    if (!this._userData) {
      this._userData = await getUserData();
    }
    return this._userData;
  }

  get user() {
    return this._user as User;
  }

  get isLoading() {
    return this._user === undefined;
  }

  get isSigned() {
    return this._user && !this._user.isAnonymous;
  }

  get userId() {
    return this._user?.uid;
  }
}